import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import SAVStamp from './SAVStamp';

interface AboutMobileProps {
  t: any;
  isRTL: boolean;
}

const AboutMobileSection = styled.section`
  min-height: 100vh;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  position: relative;
  overflow: hidden;
  padding: 1rem;
`;

const Container = styled.div`
  max-width: 100%;
  margin: 0 auto;
  position: relative;
  z-index: 1;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 2rem;
  padding: 1rem 0;
`;

const Title = styled.h2<{ $isRTL: boolean }>`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
  
  background: linear-gradient(135deg, #000000 0%, #dc2626 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  
  @media (max-width: 480px) {
    font-size: 2rem;
  }
`;

const Subtitle = styled.p<{ $isRTL: boolean }>`
  font-size: 1.1rem;
  color: #64748b;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
`;

const NavigationTabs = styled.div`
  display: flex;
  overflow-x: auto;
  gap: 0.5rem;
  margin-bottom: 2rem;
  padding: 0.5rem 0;
  scrollbar-width: none;
  -ms-overflow-style: none;
  
  &::-webkit-scrollbar {
    display: none;
  }
`;

const Tab = styled.button<{ $isActive: boolean; $isRTL: boolean }>`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 25px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
  flex-shrink: 0;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
  
  ${props => props.$isActive ? `
    background: linear-gradient(135deg, #0f172a 0%, #dc2626 100%);
    color: white;
    box-shadow: 0 4px 15px rgba(220, 38, 38, 0.3);
  ` : `
    background: white;
    color: #64748b;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  `}
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
  }
`;

const ContentArea = styled.div`
  min-height: 400px;
  position: relative;
`;

const ContentCard = styled(motion.div)`
  background: white;
  border-radius: 20px;
  padding: 2rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(220, 38, 38, 0.1);
  margin-bottom: 1rem;
`;

const ContentTitle = styled.h3<{ $isRTL: boolean }>`
  font-size: 1.8rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 1.5rem;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
`;

const ContentText = styled.p<{ $isRTL: boolean }>`
  font-size: 1rem;
  line-height: 1.7;
  color: #475569;
  margin-bottom: 1rem;
  text-align: justify;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
`;

const HighlightText = styled.span`
  background: linear-gradient(135deg, #0f172a 0%, #dc2626 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-weight: 600;
`;

const FeatureList = styled.ul<{ $isRTL: boolean }>`
  list-style: none;
  padding: 0;
  margin: 1.5rem 0;
`;

const FeatureItem = styled.li<{ $isRTL: boolean }>`
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
`;

const FeatureIcon = styled.div`
  width: 24px;
  height: 24px;
  background: linear-gradient(135deg, #0f172a 0%, #dc2626 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1rem;
  flex-shrink: 0;
  
  &::before {
    content: '✓';
    color: white;
    font-size: 0.8rem;
    font-weight: bold;
  }
`;

const FeatureText = styled.span`
  color: #475569;
  font-size: 1rem;
  line-height: 1.5;
  text-align: justify;
`;

// Mobile advantages styled components
const MobileAdvantagesContainer = styled.div<{ $isRTL: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
  position: relative;
`;

const MobileAdvantagesHeader = styled.div<{ $isRTL: boolean }>`
  text-align: center;
  margin-bottom: 1.5rem;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
`;

const MobileAdvantagesTitle = styled.h3<{ $isRTL: boolean }>`
  font-size: 1.3rem;
  font-weight: 700;
  color: #dc2626;
  margin-bottom: 0.8rem;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif"};
`;

const MobileAdvantagesSubtitle = styled.p<{ $isRTL: boolean }>`
  font-size: 1rem;
  color: #64748b;
  line-height: 1.5;
  text-align: justify;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif"};
`;

const MobileAdvantagesGrid = styled.div<{ $isRTL: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1.5rem;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
`;

const MobileAdvantageCard = styled.div<{ $isRTL: boolean }>`
  background: white;
  border-radius: 8px;
  padding: 1rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border-left: 3px solid #22c55e;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
`;

const MobileAdvantageCardTitle = styled.h4<{ $isRTL: boolean }>`
  font-size: 1rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 0.5rem;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif"};
`;

const MobileAdvantageCardText = styled.p<{ $isRTL: boolean }>`
  font-size: 0.9rem;
  color: #6b7280;
  line-height: 1.5;
  margin: 0;
  text-align: justify;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif"};
`;

const MobileSAVSection = styled.div<{ $isRTL: boolean }>`
  background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
  border-radius: 12px;
  padding: 1.5rem;
  text-align: center;
  border: 2px solid #22c55e;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
`;

const MobileSAVTitle = styled.h3<{ $isRTL: boolean }>`
  font-size: 1.2rem;
  font-weight: 700;
  color: #16a34a;
  margin-bottom: 0.8rem;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif"};
`;

const MobileSAVDescription = styled.p<{ $isRTL: boolean }>`
  font-size: 0.95rem;
  color: #166534;
  line-height: 1.5;
  margin-bottom: 1rem;
  text-align: justify;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif"};
`;

const MobileStampWrapper = styled.div<{ $isRTL: boolean }>`
  position: absolute;
  top: -0.5rem;
  ${props => props.$isRTL ? 'left: 0.5rem;' : 'right: 0.5rem;'}
  z-index: 1000;
  pointer-events: none;
`;

const AboutMobile: React.FC<AboutMobileProps> = ({ t, isRTL }) => {
  const [activeTab, setActiveTab] = useState('qui-sommes-nous');
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  const menuItems = [
    { id: 'qui-sommes-nous', title: t.aboutTabs.whoWeAre },
    { id: 'historique', title: t.aboutTabs.history },
    { id: 'positioning', title: t.aboutTabs.positioning },
    { id: 'values', title: t.aboutTabs.values },
    { id: 'legend', title: t.aboutTabs.productLegend },
    { id: 'marches-cles', title: t.aboutTabs.keyMarkets },
    { id: 'avantages', title: t.aboutTabs.advantages },
    { id: 'mission', title: t.aboutTabs.mission },
    { id: 'localisation', title: t.aboutTabs.location }
  ];

  const sections = [
    {
      id: 'qui-sommes-nous',
      title: t.aboutContent.whoWeAre.title,
      content: (
        <div>
          <ContentText $isRTL={isRTL}>
            <HighlightText>{t.techyid}, {t.semiFiliale}</HighlightText>, {t.aboutContent.whoWeAre.content1.replace(/^TECHYID, (Semi-filiale du groupe AON|Semi-subsidiary of the AON), /, '')}
          </ContentText>
          <ContentText $isRTL={isRTL}>
            {t.aboutContent.whoWeAre.content2}
          </ContentText>
          <ContentText $isRTL={isRTL}>
            {t.aboutContent.whoWeAre.content3}
          </ContentText>
          <ContentText $isRTL={isRTL}>
            {t.aboutContent.whoWeAre.content4}
          </ContentText>
          <FeatureList $isRTL={isRTL}>
            {t.aboutFeatures.whoWeAre.map((feature, index) => (
              <FeatureItem key={index} $isRTL={isRTL}>
                <FeatureIcon />
                <FeatureText>{feature}</FeatureText>
              </FeatureItem>
            ))}
          </FeatureList>
        </div>
      )
    },
    {
      id: 'historique',
      title: t.aboutContent.history.title,
      content: (
        <div>
          <ContentText $isRTL={isRTL}>
            {t.aboutContent.history.content1}
          </ContentText>
          <ContentText $isRTL={isRTL}>
            {t.aboutContent.history.content2}
          </ContentText>
          <FeatureList $isRTL={isRTL}>
            {t.aboutFeatures.history.map((feature, index) => (
              <FeatureItem key={index} $isRTL={isRTL}>
                <FeatureIcon />
                <FeatureText>{feature}</FeatureText>
              </FeatureItem>
            ))}
          </FeatureList>
        </div>
      )
    },
    {
      id: 'positioning',
      title: t.aboutContent.positioning.title,
      content: (
        <div>
          <ContentText $isRTL={isRTL}>
            {t.aboutContent.positioning.content1}
          </ContentText>
          <FeatureList $isRTL={isRTL}>
            {t.aboutFeatures.positioning.map((feature, index) => (
              <FeatureItem key={index} $isRTL={isRTL}>
                <FeatureIcon />
                <FeatureText>{feature}</FeatureText>
              </FeatureItem>
            ))}
          </FeatureList>
        </div>
      )
    },
    {
      id: 'values',
      title: t.aboutContent.values.title,
      content: (
        <div>
          <ContentText $isRTL={isRTL}>
            {t.aboutContent.values.content1}
          </ContentText>
        </div>
      )
    },
    {
      id: 'legend',
      title: t.aboutContent.productLegend.title,
      content: (
        <div>
          <ContentText $isRTL={isRTL}>
            <HighlightText>{t.techyid}</HighlightText> {t.aboutContent.productLegend.intro}
          </ContentText>
          
          <div style={{ marginTop: '1.5rem' }}>
            <ContentText $isRTL={isRTL} style={{ fontWeight: '600', color: '#1e293b', marginBottom: '0.5rem' }}>
              {t.aboutContent.productLegend.electricity.title}
            </ContentText>
            <ContentText $isRTL={isRTL} style={{ marginBottom: '0.5rem' }}>
              {t.aboutContent.productLegend.electricity.subtitle}
            </ContentText>
            <FeatureList $isRTL={isRTL}>
              <FeatureItem $isRTL={isRTL}>
                <FeatureIcon />
                <FeatureText>Câbles, connexion, terminaison et jonction</FeatureText>
              </FeatureItem>
              <FeatureItem $isRTL={isRTL}>
                <FeatureIcon />
                <FeatureText>Goulottes, conduits et accessoires</FeatureText>
              </FeatureItem>
              <FeatureItem $isRTL={isRTL}>
                <FeatureIcon />
                <FeatureText>Électroménager</FeatureText>
              </FeatureItem>
              <FeatureItem $isRTL={isRTL}>
                <FeatureIcon />
                <FeatureText>Éclairage et sources</FeatureText>
              </FeatureItem>
              <FeatureItem $isRTL={isRTL}>
                <FeatureIcon />
                <FeatureText>Matériel industriel</FeatureText>
              </FeatureItem>
              <FeatureItem $isRTL={isRTL}>
                <FeatureIcon />
                <FeatureText>Matériel basse tension</FeatureText>
              </FeatureItem>
              <FeatureItem $isRTL={isRTL}>
                <FeatureIcon />
                <FeatureText>Sécurité, détection incendie et parafoudres</FeatureText>
              </FeatureItem>
              <FeatureItem $isRTL={isRTL}>
                <FeatureIcon />
                <FeatureText>Instrumentation, mesure et régulation</FeatureText>
              </FeatureItem>
              <FeatureItem $isRTL={isRTL}>
                <FeatureIcon />
                <FeatureText>Matériel de poste et matériel de ligne</FeatureText>
              </FeatureItem>
              <FeatureItem $isRTL={isRTL}>
                <FeatureIcon />
                <FeatureText>Groupes électrogènes</FeatureText>
              </FeatureItem>
              <FeatureItem $isRTL={isRTL}>
                <FeatureIcon />
                <FeatureText>Chargeurs de batteries</FeatureText>
              </FeatureItem>
              <FeatureItem $isRTL={isRTL}>
                <FeatureIcon />
                <FeatureText>Armoires et coffrets informatiques</FeatureText>
              </FeatureItem>
              <FeatureItem $isRTL={isRTL}>
                <FeatureIcon />
                <FeatureText>Systèmes courants faibles</FeatureText>
              </FeatureItem>
            </FeatureList>

            <ContentText $isRTL={isRTL} style={{ fontWeight: '600', color: '#1e293b', marginBottom: '0.5rem', marginTop: '1.5rem' }}>
              {t.aboutContent.productLegend.industry.title}
            </ContentText>
            <ContentText $isRTL={isRTL} style={{ marginBottom: '0.5rem' }}>
              {t.aboutContent.productLegend.industry.subtitle}
            </ContentText>
            <FeatureList $isRTL={isRTL}>
              <FeatureItem $isRTL={isRTL}>
                <FeatureIcon />
                <FeatureText>Mines</FeatureText>
              </FeatureItem>
              <FeatureItem $isRTL={isRTL}>
                <FeatureIcon />
                <FeatureText>Pétrole et gaz</FeatureText>
              </FeatureItem>
              <FeatureItem $isRTL={isRTL}>
                <FeatureIcon />
                <FeatureText>Électrique</FeatureText>
              </FeatureItem>
              <FeatureItem $isRTL={isRTL}>
                <FeatureIcon />
                <FeatureText>Chimique</FeatureText>
              </FeatureItem>
              <FeatureItem $isRTL={isRTL}>
                <FeatureIcon />
                <FeatureText>Agroalimentaire</FeatureText>
              </FeatureItem>
              <FeatureItem $isRTL={isRTL}>
                <FeatureIcon />
                <FeatureText>Briqueteries</FeatureText>
              </FeatureItem>
              <FeatureItem $isRTL={isRTL}>
                <FeatureIcon />
                <FeatureText>Cimenteries</FeatureText>
              </FeatureItem>
            </FeatureList>

            <ContentText $isRTL={isRTL} style={{ fontWeight: '600', color: '#1e293b', marginBottom: '0.5rem', marginTop: '1.5rem' }}>
              {t.aboutContent.productLegend.building.title}
            </ContentText>
            <ContentText $isRTL={isRTL} style={{ marginBottom: '0.5rem' }}>
              {t.aboutContent.productLegend.building.subtitle}
            </ContentText>
            <FeatureList $isRTL={isRTL}>
              <FeatureItem $isRTL={isRTL}>
                <FeatureIcon />
                <FeatureText>Étanchéité</FeatureText>
              </FeatureItem>
              <FeatureItem $isRTL={isRTL}>
                <FeatureIcon />
                <FeatureText>Plâtre</FeatureText>
              </FeatureItem>
              <FeatureItem $isRTL={isRTL}>
                <FeatureIcon />
                <FeatureText>Carrelage</FeatureText>
              </FeatureItem>
              <FeatureItem $isRTL={isRTL}>
                <FeatureIcon />
                <FeatureText>Bardage</FeatureText>
              </FeatureItem>
              <FeatureItem $isRTL={isRTL}>
                <FeatureIcon />
                <FeatureText>Sanitaire</FeatureText>
              </FeatureItem>
              <FeatureItem $isRTL={isRTL}>
                <FeatureIcon />
                <FeatureText>Robinetterie</FeatureText>
              </FeatureItem>
              <FeatureItem $isRTL={isRTL}>
                <FeatureIcon />
                <FeatureText>Tôles, aciers et tubes</FeatureText>
              </FeatureItem>
              <FeatureItem $isRTL={isRTL}>
                <FeatureIcon />
                <FeatureText>Portes</FeatureText>
              </FeatureItem>
            </FeatureList>

            <ContentText $isRTL={isRTL} style={{ fontWeight: '600', color: '#1e293b', marginBottom: '0.5rem', marginTop: '1.5rem' }}>
              {t.aboutContent.productLegend.infrastructure.title}
            </ContentText>
            <ContentText $isRTL={isRTL} style={{ marginBottom: '0.5rem' }}>
              {t.aboutContent.productLegend.infrastructure.subtitle}
            </ContentText>
          <FeatureList $isRTL={isRTL}>
            <FeatureItem $isRTL={isRTL}>
              <FeatureIcon />
                <FeatureText>Électrification et électrification rurale</FeatureText>
              </FeatureItem>
              <FeatureItem $isRTL={isRTL}>
                <FeatureIcon />
                <FeatureText>Aéroports</FeatureText>
              </FeatureItem>
              <FeatureItem $isRTL={isRTL}>
                <FeatureIcon />
                <FeatureText>Ports</FeatureText>
            </FeatureItem>
            <FeatureItem $isRTL={isRTL}>
              <FeatureIcon />
                <FeatureText>Chemins de fer</FeatureText>
            </FeatureItem>
            <FeatureItem $isRTL={isRTL}>
              <FeatureIcon />
                <FeatureText>Hydraulique</FeatureText>
            </FeatureItem>
            <FeatureItem $isRTL={isRTL}>
              <FeatureIcon />
                <FeatureText>Télécom</FeatureText>
            </FeatureItem>
          </FeatureList>
          </div>
        </div>
      )
    },
    {
      id: 'marches-cles',
      title: t.aboutContent.keyMarkets.title,
      content: (
        <div>
          <ContentText $isRTL={isRTL}>
            <strong>{t.aboutContent.keyMarkets.building.title}</strong><br/>
            {t.aboutContent.keyMarkets.building.content}
          </ContentText>
          <ContentText $isRTL={isRTL}>
            <strong>{t.aboutContent.keyMarkets.industry.title}</strong><br/>
            {t.aboutContent.keyMarkets.industry.content}
          </ContentText>
          <ContentText $isRTL={isRTL}>
            <strong>{t.aboutContent.keyMarkets.infrastructure.title}</strong><br/>
            {t.aboutContent.keyMarkets.infrastructure.content}
          </ContentText>
          <ContentText $isRTL={isRTL}>
            <strong>{t.techyid}</strong>, {t.aboutContent.keyMarkets.conclusion}
          </ContentText>
        </div>
      )
    },
    {
      id: 'avantages',
      title: t.aboutContent.advantages.title,
      content: (
        <MobileAdvantagesContainer $isRTL={isRTL}>
          <MobileStampWrapper $isRTL={isRTL}>
            <SAVStamp size={140} />
          </MobileStampWrapper>
          
          <MobileAdvantagesHeader $isRTL={isRTL}>
            <MobileAdvantagesSubtitle $isRTL={isRTL}>
              Découvrez les nombreux avantages qui font de TECHYID votre partenaire de confiance
            </MobileAdvantagesSubtitle>
          </MobileAdvantagesHeader>

          <MobileAdvantagesGrid $isRTL={isRTL}>
            <MobileAdvantageCard $isRTL={isRTL}>
              <MobileAdvantageCardTitle $isRTL={isRTL}>
                💰 Économies d'Échelle
              </MobileAdvantageCardTitle>
              <MobileAdvantageCardText $isRTL={isRTL}>
                Des prix compétitifs négociés au niveau national et international
              </MobileAdvantageCardText>
            </MobileAdvantageCard>

            <MobileAdvantageCard $isRTL={isRTL}>
              <MobileAdvantageCardTitle $isRTL={isRTL}>
                🛡️ Sécurité d'Approvisionnement
              </MobileAdvantageCardTitle>
              <MobileAdvantageCardText $isRTL={isRTL}>
                Garantie de qualité et de disponibilité des produits
              </MobileAdvantageCardText>
            </MobileAdvantageCard>

            <MobileAdvantageCard $isRTL={isRTL}>
              <MobileAdvantageCardTitle $isRTL={isRTL}>
                🎯 Savoir-Faire TECHYID
              </MobileAdvantageCardTitle>
              <MobileAdvantageCardText $isRTL={isRTL}>
                Expertise technique reconnue dans le domaine électrique
              </MobileAdvantageCardText>
            </MobileAdvantageCard>

            <MobileAdvantageCard $isRTL={isRTL}>
              <MobileAdvantageCardTitle $isRTL={isRTL}>
                🌍 Partenariats Mondiaux
              </MobileAdvantageCardTitle>
              <MobileAdvantageCardText $isRTL={isRTL}>
                Collaboration avec les plus grands fabricants mondiaux
              </MobileAdvantageCardText>
            </MobileAdvantageCard>

            <MobileAdvantageCard $isRTL={isRTL}>
              <MobileAdvantageCardTitle $isRTL={isRTL}>
                ⚡ Disponibilité Immédiate
              </MobileAdvantageCardTitle>
              <MobileAdvantageCardText $isRTL={isRTL}>
                Mise en disponibilité rapide des produits
              </MobileAdvantageCardText>
            </MobileAdvantageCard>

            <MobileAdvantageCard $isRTL={isRTL}>
              <MobileAdvantageCardTitle $isRTL={isRTL}>
                📋 Assistance Marchés
              </MobileAdvantageCardTitle>
              <MobileAdvantageCardText $isRTL={isRTL}>
                Support complet pour la soumission à des marchés
              </MobileAdvantageCardText>
            </MobileAdvantageCard>
          </MobileAdvantagesGrid>
        </MobileAdvantagesContainer>
      )
    },
    {
      id: 'mission',
      title: t.aboutContent.mission.title,
      content: (
        <div>
          <ContentText $isRTL={isRTL}>
            {t.aboutContent.mission.content1}
          </ContentText>
          <ContentText $isRTL={isRTL}>
            <strong>{t.aboutContent.mission.valuesTitle}</strong>
          </ContentText>
          <FeatureList $isRTL={isRTL}>
            {t.aboutFeatures.mission.map((feature, index) => (
              <FeatureItem key={index} $isRTL={isRTL}>
                <FeatureIcon />
                <FeatureText>{feature}</FeatureText>
              </FeatureItem>
            ))}
          </FeatureList>
        </div>
      )
    },
    {
      id: 'localisation',
      title: t.aboutContent.location.title,
      content: (
        <div>
          <ContentText $isRTL={isRTL}>
            <strong>{t.techyid}</strong> {t.aboutContent.location.content1}
          </ContentText>
          <div style={{ marginTop: '1.5rem', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)' }}>
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3791.9513070339995!2d-16.0008326!3d18.1200397!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xe964dc2ccbd936d%3A0x2754540aab4bf46a!2sTechyid%20Mauritanie!5e0!3m2!1sen!2s!4v1759489683068!5m2!1sen!2s"
              width="100%"
              height="300"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Localisation TECHYID - Tevragh Zeina, Cité plage, lot 41, Nouakchott"
            ></iframe>
          </div>
          <div style={{ marginTop: '1rem', textAlign: 'center' }}>
            <a
              href="https://maps.app.goo.gl/496c4m5JTW76c3ie8"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: '#dc2626',
                textDecoration: 'none',
                fontWeight: '600',
                padding: '0.75rem 1.5rem',
                border: '2px solid #dc2626',
                borderRadius: '8px',
                display: 'inline-block',
                transition: 'all 0.3s ease'
              }}
              onMouseOver={(e) => {
                (e.target as HTMLAnchorElement).style.backgroundColor = '#dc2626';
                (e.target as HTMLAnchorElement).style.color = 'white';
              }}
              onMouseOut={(e) => {
                (e.target as HTMLAnchorElement).style.backgroundColor = 'transparent';
                (e.target as HTMLAnchorElement).style.color = '#dc2626';
              }}
            >
              {t.aboutContent.location.mapButton}
            </a>
          </div>
        </div>
      )
    }
  ];

  const currentSection = sections.find(section => section.id === activeTab);

  return (
    <AboutMobileSection id="about-mobile" ref={ref}>
      <Container>
        <Header>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <Title $isRTL={isRTL}>{t.aboutTitle}</Title>
          </motion.div>
        </Header>

        <NavigationTabs>
          {menuItems.map((item, index) => (
            <Tab
              key={item.id}
              $isActive={activeTab === item.id}
              $isRTL={isRTL}
              onClick={() => setActiveTab(item.id)}
            >
              {item.title}
            </Tab>
          ))}
        </NavigationTabs>

        <ContentArea>
          <AnimatePresence mode="wait">
            {currentSection && (
              <ContentCard
                key={activeTab}
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                transition={{ duration: 0.5 }}
              >
                <ContentTitle $isRTL={isRTL}>{currentSection.title}</ContentTitle>
                {currentSection.content}
              </ContentCard>
            )}
          </AnimatePresence>
        </ContentArea>
      </Container>
    </AboutMobileSection>
  );
};

export default AboutMobile;
